import React, { ReactElement, useEffect, useRef, useState } from 'react';
import addAlert from 'ps-components/AlertToast/addAlert';
import moment, { Moment } from "moment";
import { useHistory } from 'react-router-dom';
import { useMasterAccountId } from '../../../../hooks/redux';
import { Link } from 'react-router-dom';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import { FormGroup, Toggle } from 'ps-design';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import { INITIAL_SURVEY_STATE, STATIC_SURVEY_INPUTS } from '../constants';
import { getFormGroupLabelForRequiredField, renderTextArea, renderInput, renderSelect } from '../helpers';
import { getIndustries, getGeographies, savePrivateSurvey } from '../PrivateSurveyJobMatchService';
import { IOption, ISavePrivateSurveyParams } from '../types/privateSurvey';
import './PrivateSurvey.scss';
import { DatePickerInput } from '@mantine/dates';

const PrivateSurvey = (props: {
  hideSlideout: () => void;
  fromIframe: boolean;
  navigateToPrevSlideout: (arg0?: string | React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  refreshPrivateSurveyTabData: () => void;
}): ReactElement => {
  const { hideSlideout, fromIframe, navigateToPrevSlideout, refreshPrivateSurveyTabData } = props;
  const { subscribeAll, surveyCode, surveyEffectiveDate, surveyGeography, surveyIndustry, surveyName, surveyOverview, surveyPublisher } = STATIC_SURVEY_INPUTS;
  const history = useHistory();
  const masterAccountId = useMasterAccountId();
  const [surveyFields, setSurveyFields] = useState<ISavePrivateSurveyParams>(INITIAL_SURVEY_STATE);
  const [datePickerFocus, setDatePickerFocus] = useState<boolean>(false);
  const [geographies, setGeographies] = useState<IOption[]>([]);
  const [industries, setIndustries] = useState<IOption[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isSurveyCodeValid = !surveyFields.pub_survey_code?.match(/.*\d{2}$/) || !(surveyFields.pub_survey_code?.length >= 3);
  const isSaveDisabled =
    !surveyFields.pub_survey_code ||
    !surveyFields.pub_survey_effective_date ||
    !surveyFields.pub_survey_short_name ||
    !surveyFields.pub_survey_publisher ||
    isSurveyCodeValid;

  useEffect(() => {
    getGeographyValues();
    getIndustryValues();
  }, []);

  const clearInputs = () => {
    setSurveyFields(INITIAL_SURVEY_STATE)
  }

  const getGeographyValues = async () => {
    const options = await getGeographies(masterAccountId);
    setGeographies(options?.data?.map((option: string) => {
      return { text: option, value: option }
    }))
  }

  const getIndustryValues = async () => {
    const options = await getIndustries(masterAccountId)
    setIndustries(options?.data?.map((option: string) => {
      return { text: option, value: option }
    }))
  }

  const handleEffectiveDateOnChange = (value:any) => {
    setSurveyFields({...surveyFields, pub_survey_effective_date: value })
  }


  const handleInputUpdate = (id: string, value: string) => {
    if (id === 'pub_survey_short_name') setSurveyFields({...surveyFields, [id]: value, pub_survey_long_name: value})
    else setSurveyFields({...surveyFields, [id]: value})
  }

  const handleSelectUpdate = (params: { id: string, value: string }) => {
    const { id, value } = params;
    setSurveyFields({...surveyFields, [id]: value})
  }

  const isOutsideRange = () => {
    return false;
  }

  const onClose = () => {
    clearInputs();
    fromIframe ? hideSlideout() : navigateToPrevSlideout();
    if (fromIframe) refreshPrivateSurveyTabData();
  }

  const onSave = () => {
    setIsSaving(true);
    savePrivateSurvey(masterAccountId, surveyFields)
      .then((resp) => {
        if (resp?.status === 409 || resp?.status === 403) {
          setIsSaving(false);
          addAlert(resp?.detail, 'warning', 5000, 'top-center');
          return;
        }

        setIsSaving(false);
        addAlert('Successfully added survey', 'success', 5000, 'top-center');
        if (!fromIframe) navigateToPrevSlideout(surveyFields.pub_survey_code);
      }).catch(e => {
        setIsSaving(false);
        addAlert('Error adding survey', 'danger', 5000, 'top-center');
      });
  }

  const renderDatePicker = () => {
   const date = surveyFields.pub_survey_effective_date ? new Date(surveyFields.pub_survey_effective_date) : null;
    return (
      <div className="col-md-6">
        <FormGroup>
          {getFormGroupLabelForRequiredField(surveyEffectiveDate.label)}
          <DatePickerInput
            size="xl"
            id={surveyEffectiveDate.id}
            value={date}
            onChange={handleEffectiveDateOnChange}
            placeholder=""
          />
        </FormGroup>
      </div>
    )
  }

  const renderGroupToggle = () => {
    return (
      <div className="group-toggle__container">
        <Toggle
          className="group-toggle"
          id={subscribeAll.id}
          onChange={handleInputUpdate}
          value={surveyFields.apply_sub_to_subgroups}
        />
        <span className="group-toggle__text">{subscribeAll.label}</span>
      </div>
    )
  }

  return (
    <React.Fragment>
     <ProgressStatusOverlay
        inProgress={isSaving}
        primaryText='Saving Survey Details...'
        submitSuccessDetails="This might take a moment"
      />
      <div className="private-survey__container">
        <SlideoutHeader title="Add Private Survey" doClose={onClose} />
        {!fromIframe && (
          <div className="private-survey__sub-header">
           <Link
              className="return__link"
              to={{ pathname: history.location.pathname, hash: '#private-survey-job-match' }}
              onClick={navigateToPrevSlideout}
            >
              <i className="icon-reply" />
              Return to Add a Private Survey Job
            </Link>
          </div>
        )}
        <ActionableHeaderCard cardTitle="Survey Details" className="private-survey__card" customActionNodes={renderGroupToggle()} isCollapsible={false}>
      	  <div className="row">
            {renderInput({
              error: !isSurveyCodeValid || !surveyFields.pub_survey_code ? null : " ",
              label: surveyCode.label,
              id: surveyCode.id,
              onChange: handleInputUpdate,
              required: true,
              value: surveyFields.pub_survey_code
            })}
            {renderInput({
              label: surveyPublisher.label,
              id: surveyPublisher.id,
              onChange: handleInputUpdate,
              required: true,
              value: surveyFields.pub_survey_publisher
            })}
      	  </div>
      	  <div className="row">
            {renderInput({
              label: surveyName.label,
              id: surveyName.id,
              onChange: handleInputUpdate,
              required: true,
              value: surveyFields.pub_survey_short_name
            })}
            {renderDatePicker()}
      	  </div>
      	 <div className="row">
            <div className="col-md-6">
              {renderSelect({
                label: surveyGeography.label,
                id: surveyGeography.id,
                options: geographies,
                onChange: handleSelectUpdate,
                value: surveyFields.geography
              })}
            </div>
            <div className="col-md-6">
              {renderSelect({
                label: surveyIndustry.label,
                id: surveyIndustry.id,
                options: industries,
                onChange: handleSelectUpdate,
                value: surveyFields.industry
              })}
      	    </div>
          </div>
          <div className="row">
            {renderTextArea({
              label: surveyOverview.label,
              id: surveyOverview.id,
              onChange: handleInputUpdate,
              rows: 3,
              value: surveyFields.survey_overview
            })}
      	  </div>
      	</ActionableHeaderCard>
      </div>
      <SlideoutFooter isBoxShadowTheme={true}>
        <SlideoutFooterStandardButtons
          applyText="Save"
          doApply={onSave}
          doCancel={onClose}
          isApplyDisabled={isSaveDisabled}
        />
      </SlideoutFooter>
    </React.Fragment>
  );
}

export default PrivateSurvey;
